import React from "react";
import { UserGroupIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { useTheme } from "../../contexts/ThemeContext";

const Sidebar = ({
  sidebarOpen,
  setSidebarOpen,
  navigationItems,
  activeTab,
  setActiveTab,
  setSelectedUser,
  handleLogout,
}) => {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  return (
    <div
      className={`fixed inset-y-0 left-0 z-50 w-80 dark-sidebar transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:relative lg:flex-shrink-0 lg:block ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      }`}
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-accent-grad rounded-xl flex items-center justify-center shadow-lg">
              <UserGroupIcon className="w-7 h-7 heading" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Сату менеджері</h1>
              <p className="text-sm text-text-secondary">Басқару панелі</p>
            </div>
          </div>

          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-lg text-text-secondary hover:text-white hover:bg-bg-primary"
          >
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <div className="flex-1 p-6 overflow-y-auto">
          <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-4">
            Навигация
          </h2>

          <nav className="space-y-2">
            {navigationItems.map((item, index) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    setSelectedUser(null);
                  }}
                  className={`w-full flex items-center justify-between p-4 rounded-xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden ${
                    isActive ? "text-text-primary" : "bg-transparent hover:bg-bg-tertiary text-text-secondary"
                  }`}
                  style={{
                    animationDelay: `${index * 0.1}s`,
                    ...(isActive && {
                      background: isLight
                        ? 'rgba(255,255,255,1)'
                        : 'rgba(255,255,255,0.03)',
                      border: isLight
                        ? '1px solid rgba(0,0,0,0.1)'
                        : '1px solid rgba(255,255,255,0.1)',
                    }),
                  }}
                >
                  {isActive && (
                    <div
                      className="absolute inset-0 rounded-xl pointer-events-none"
                      style={{
                        background: 'linear-gradient(135deg, rgba(79,69,229,0.01) 0%, rgba(124,58,237,0.01) 100%)',
                      }}
                    />
                  )}

                  <div className="flex items-center space-x-3 relative z-10">
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                        isActive
                          ? "bg-accent-grad text-white"
                          : `border shadow-sm ${isLight ? 'bg-[#F1F3F5] border-black/10' : 'bg-[#2E323B] border-white/10'}`
                      }`}
                    >
                      <Icon
                        className={`w-5 h-5 ${
                          isActive
                            ? "text-white stroke-white"
                            : "sidebar-icon-inactive"
                        }`}
                      />
                    </div>

                    <div className="text-left">
                      <p className="font-medium">{item.name}</p>
                      <p className="text-xs opacity-75">{item.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Logout */}
        <div className="p-6 border-t border-gray-700/50">
          <button
            onClick={handleLogout}
            className="flex items-center group transition-opacity hover:opacity-80"
          >
            <div className="relative w-8 h-8 flex items-center justify-center">
              <div
                className="absolute inset-0 rounded-[8px]"
                style={{
                  background: "linear-gradient(270deg, #FF0077 0%, #FF1A00 100%)",
                  WebkitMask:
                    "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                  mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                  WebkitMaskComposite: "xor",
                  maskComposite: "exclude",
                  padding: "1px",
                }}
              />
              <div
                className="absolute inset-0 rounded-[8px] opacity-10"
                style={{
                  background: "linear-gradient(270deg, #FF0077 0%, #FF1A00 100%)",
                }}
              />
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                className="relative z-10"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.5 9.5L9.5 2.5M2.5 2.5L9.5 9.5"
                  stroke="url(#logout_grad_v3)"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient
                    id="logout_grad_v3"
                    x1="0"
                    y1="6"
                    x2="12"
                    y2="6"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#FF0077" />
                    <stop offset="1" stopColor="#FF1A00" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <span className="ml-3 font-semibold text-[16px] leading-[24px] bg-gradient-to-r from-[#FF0077] to-[#FF1A00] bg-clip-text text-transparent">
              Шығу
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
